<script setup>
import { computed, ref } from 'vue';
import Button from 'dashboard/components-next/button/Button.vue';
import { formatFieldValue } from '../formatFieldValue';

const props = defineProps({
  // The field labels as they were when the task was created, and the answers.
  fields: { type: Array, required: true },
  data: { type: Object, required: true },
  // A CPF is always masked; only the card page lets the agent reveal it, never the conversation.
  revealable: { type: Boolean, default: false },
});

const revealedKeys = ref([]);
const isRevealed = key => revealedKeys.value.includes(key);
const toggleReveal = key => {
  revealedKeys.value = isRevealed(key)
    ? revealedKeys.value.filter(item => item !== key)
    : [...revealedKeys.value, key];
};

// Only the fields that were filled in.
const answers = computed(() =>
  props.fields
    .map(field => ({
      key: field.key,
      label: field.label || field.key,
      value: formatFieldValue(field, props.data[field.key], {
        revealed: isRevealed(field.key),
      }),
      canReveal: props.revealable && field.type === 'cpf',
    }))
    .filter(field => field.value !== '')
);
</script>

<template>
  <dl
    v-if="answers.length"
    class="grid grid-cols-2 p-4 mb-0 border gap-x-6 gap-y-4 rounded-xl border-n-weak"
  >
    <div
      v-for="answer in answers"
      :key="answer.key"
      class="flex flex-col gap-0.5 min-w-0"
      :class="{ 'col-span-2': answer.key === 'description' }"
      :data-testid="`card-form-${answer.key}`"
    >
      <dt class="text-xs text-n-slate-11">{{ answer.label }}</dt>
      <dd
        class="flex items-center gap-1 mb-0 text-sm break-words text-n-slate-12"
      >
        <span class="min-w-0" data-testid="card-form-value">
          {{ answer.value }}
        </span>
        <Button
          v-if="answer.canReveal"
          :icon="isRevealed(answer.key) ? 'i-lucide-eye-off' : 'i-lucide-eye'"
          slate
          ghost
          xs
          :title="
            $t(
              isRevealed(answer.key)
                ? 'CARDS.TASKS.HIDE_VALUE'
                : 'CARDS.TASKS.SHOW_VALUE'
            )
          "
          data-testid="card-form-reveal"
          @click="toggleReveal(answer.key)"
        />
      </dd>
    </div>
  </dl>
  <p v-else class="mb-0 text-sm text-n-slate-10">
    {{ $t('CARDS.SHOW.NO_FORM') }}
  </p>
</template>
